import notificationService from '@/services/notification/notification.service'
import organizationService from '@/services/amio-api/organization.service'
import $script from 'scriptjs'
import merge from 'ramda/src/merge'
import path from 'ramda/src/path'
import axios from 'axios'
import facebookPermission from './facebook-permission.enum'

const fbHttpClient = axios.create({
  baseURL: 'https://graph.facebook.com/v3.0/',
  headers: {'Accept': 'application/json'}
})

class FacebookService {
  constructor() {
    this.fbConnector = null
    this.fbAppId = null
  }

  getFbConnector() {
    if (this.fbConnector) {
      return new Promise(resolve => {
        resolve(this.fbConnector)
      })
    }

    const loadFbSDK = new Promise(resolve => {
      /* eslint no-undef: "off" */
      if (window.FB) {
        log.debug('FB object was already loaded')
        resolve()
        return
      }

      // facebook requires to process with callback on window object
      window.fbAsyncInit = function () {
        log.debug('fbAsyncInit called')
        resolve()
      }

      // download facebook sdk
      $script(process.env.VUE_APP_FACEBOOK_SDK_URL)
    })

    return Promise.all([loadFbSDK, this.getAppId()])
      .then((values) => {
        const appId = values[1]
        /* eslint no-undef: "off" */
        this.fbConnector = FB
        // init facebook
        this.fbConnector.init({
          appId: appId,
          cookie: true,
          status: true,
          xfbml: true,
          version: process.env.VUE_APP_FACEBOOK_API_VERSION
        })

        this.fbConnector.AppEvents.logPageView()

        return this.fbConnector
      })
  }

  getAppId() {
    if (this.fbAppId) {
      return Promise.resolve(this.fbAppId)
    }

    return organizationService.get()
      .then(organization => {
        if (organization.facebook_app) {
          return organization.facebook_app.id
        }

        return process.env.VUE_APP_FACEBOOK_APP_ID
      })
      .then(fbAppId => {
        this.fbAppId = fbAppId
        return this.fbAppId
      })
  }

  getLoginStatus() {
    return this.resolveFbCall((fbConnector, resolve) => {
      fbConnector.getLoginStatus(resolve)
    })
  }

  listFacebookAccounts(params = {}) {
    const _params = merge({offset: 0, max: parseInt(process.env.VUE_APP_LIST_MAX_SIZE), summary: 'total_count'}, params)
    // using facebook naming
    _params.limit = _params.max
    return this.resolveFbCall((fbConnector, resolve) => {
      fbConnector.api('/me/accounts', _params, resolve)
    })
  }

  getPageInfo(pageId) {
    return this.resolveFbCall((fbConnector, resolve) => {
      fbConnector.api(`/${pageId}`, { fields: 'id,name,access_token' }, resolve)
    })
  }

  getUserInfo(id) {
    return this.resolveFbCall((fbConnector, resolve) => {
      fbConnector.api(`/${id}`, resolve)
    })
  }

  // By default this edge will return a 302 redirect to the picture image. To get access to the data about the picture, please include redirect=false in your query.
  getUserPicture(id) {
    return this.resolveFbCall((fbConnector, resolve) => {
      fbConnector.api(`/${id}/picture?redirect=false`, resolve)
    })
  }

  /**
   * @param valueToSum see https://developers.facebook.com/docs/reference/javascript/FB.AppEvents.LogEvent
   */
  logEvent(eventName, params, valueToSum) {
    this.fbConnector.AppEvents.logEvent(eventName, null, params)
  }

  parse() {
    return this.resolveFbCall((fbConnector, resolve) => {
      resolve(fbConnector.XFBML.parse())
    })
  }

  login() {
    const permissions = 'email,pages_show_list,pages_messaging,pages_read_engagement,business_management'
    return this.resolveFbCall((fbConnector, resolve) => {
      fbConnector.login(resolve, {scope: permissions})
    })
      .then(response => {
        if (!response.authResponse) {
          log.error('facebook login failed', response)
          throw new Error('Facebook login failed', response)
        }

        log.debug('facebook login response', response)
        return response
      })
      .catch(error => notificationService.error(error))
  }

  logout() {
    return this.resolveFbCall((fbConnector, resolve) => {
      fbConnector.logout(resolve)
    })
  }

  permissionDelete() {
    return this.resolveFbCall((fbConnector, resolve) => {
      fbConnector.api('/me/permissions', 'delete', resolve)
    })
  }

  setSendToMessengerPluginRenderedCallback(callback) {
    // this fore facebook to be re-initiated - Fb.init() - otherwise new event is not registered if there was one already
    this.fbConnector = null

    return this.getFbConnector()
      .then((fbConnector) => {
        fbConnector.Event.subscribe('send_to_messenger', function (event) {
          log.info('FacebookService - Event "send_to_messenger"', event)

          if (event.event === 'rendered') {
            log.debug('FacebookService - Event "send_to_messenger" - Plugin was rendered')
            callback()
          } else if (event.event === 'not_you') {
            log.debug('User clicked \'not you\'')
          }
        })
      })
      .catch(error => notificationService.error(error))
  }

  resolveFbCall(callback) {
    log.debug(`FB call - start`, callback)
    return new Promise(resolve => {
      this.getFbConnector()
        .then((fbConnector) => {
          callback(fbConnector, value => {
            log.debug(`FB call - end`, callback)
            resolve(value)
          })
        })
        .catch(error => notificationService.error(error))
    })
  }

  /**
   * Uses the access token that is saved with the FB page to check if the permissions are alright
   * @param fbPage
   * @returns {Promise} Returns OK if everything is ok, or proper error code otherwise.
   */
  checkPermissions(fbPage) {
    const config = {
      params: {
        access_token: fbPage.access_token,
        fields: 'id,name,access_token'
      }
    }

    return fbHttpClient.get(fbPage.id, config)
      .then(function (response) {
        return response.data.access_token !== null ? facebookPermission.OK : facebookPermission.GENERIC_ERROR
      })
      .catch((err) => {
        // So facebook actually has an error code in the response body
        // But it is the same for both invalid token and manage_pages error. OMFG.
        // So let's go with checking error messages. Wonder how long till it breaks...
        const errorMessage = path(['response', 'data', 'error', 'message'], err)
        if (!errorMessage) {
          return facebookPermission.GENERIC_ERROR
        } else if (errorMessage.toLowerCase().startsWith('invalid oauth access token')) {
          return facebookPermission.INVALID_ACCESS_TOKEN
        } else if (errorMessage.toLowerCase().startsWith("the 'manage_pages' permission must be granted")) {
          return facebookPermission.MANAGE_PAGES_NOT_ALLOWED
        }

        return facebookPermission.GENERIC_ERROR
      })
  }
}

export default new FacebookService()
